export interface RulesConfig {
  version: number;
  ignoreEvents?: string[];
  ignoreProperties: {
    forAllEvents: string[];
    [category: string]: string[];
  };
  ignorePropertyForAllEventsIf: Record<string, unknown>;
  ignorePropertyForAllEventsWhoseValueIs: unknown[];
  ignorePropertiesOfSDK?: string[];
  categoryMapping?: {
    ignorePropertiesForSpecificEvents: Record<string, string[]>;
  };
}

export type AnalyticsPayload = Record<string, unknown>;

/**
 * Checks if the entire event should be dropped.
 */
export const shouldIgnoreEvent = (
  eventName: string,
  config: RulesConfig
): boolean => {
  return (config.ignoreEvents || []).includes(eventName);
};

/**
 * Resolves categories into a flat list of property keys.
 */
const resolveCategories = (
  categories: string[],
  config: RulesConfig
): string[] => {
  return categories.reduce((acc: string[], cat: string) => {
    return [...acc, ...(config.ignoreProperties[cat] || [])];
  }, []);
};

/**
 * Main filtering logic
 */
export const filterProperties = (
  eventName: string,
  payload: AnalyticsPayload,
  config: RulesConfig
): AnalyticsPayload => {
  const cleaned: AnalyticsPayload = { ...payload };

  // 1. Event-specific categories (safe access)
  const eventCategories =
    config.categoryMapping?.ignorePropertiesForSpecificEvents?.[eventName] ||
    [];

  const eventSpecificToIgnore = resolveCategories(eventCategories, config);

  // 2. Global ignore lists (safe defaults)
  const globalsToIgnore = [
    ...(config.ignoreProperties?.forAllEvents || []),
    ...(config.ignorePropertiesOfSDK || []),
    ...eventSpecificToIgnore
  ];

  const ignoreValues = config.ignorePropertyForAllEventsWhoseValueIs || [];

  const conditionalIgnore = config.ignorePropertyForAllEventsIf || {};

  // 3. Process payload
  Object.keys(cleaned).forEach((key) => {
    const value = cleaned[key];

    // Remove empty values
    if (value === undefined || value === '') {
      delete cleaned[key];
      return;
    }

    // Static key removal
    if (globalsToIgnore.includes(key)) {
      delete cleaned[key];
      return;
    }

    // Value-based removal
    if (ignoreValues.includes(value)) {
      delete cleaned[key];
      return;
    }

    // Conditional key-value match removal
    if (
      Object.prototype.hasOwnProperty.call(conditionalIgnore, key) &&
      conditionalIgnore[key] === value
    ) {
      delete cleaned[key];
    }
  });

  return cleaned;
};

/**
 * Processes an event.
 * Returns the filtered payload, or null if the event should be ignored.
 */
export function processEvent(
  eventName: string,
  payload: AnalyticsPayload,
  config: RulesConfig
): AnalyticsPayload | null {
  try {
    if (shouldIgnoreEvent(eventName, config)) {
      return null;
    }

    return filterProperties(eventName, payload, config);
  } catch (error) {
    console.error('[Analytics Engine] Error processing rules', error);
    // Fallback: return original payload if engine fails to avoid data loss
    return payload;
  }
}
